import React, { useEffect, useState } from "react";
import {
    IconButton,
    LinearProgress,
    Popover,
    SelectChangeEvent,
    Stack,
    SvgIcon,
    Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import StarIcon from "@mui/icons-material/Star";

import { RootState } from "../../store";
import { resetVideo, addMultiViewSlot } from "../../store/player/playerSlice";

import { usePlayerControls } from "../../hooks/Videos/usePlayerControls";
import { useProgramChannels } from "../../hooks/Videos/useProgramChannels";
import useResize from "../../hooks/useResize";

import {
    useAddToListMutation,
    useDeleteToListMutation,
    useLazyGetListQuery,
} from "../../api/modules";

import { Button, CustomSelect, ListTV } from "../index";
import EpisodeCard from "../Cards/EpisodeCard/EpisodeCard";

import {
    Arrow,
    BurgerClose,
    FullScreen,
    PlayerBack,
    PlayerBackArrow,
    PlayerLayout,
    PlayerMessage,
    PlayerMinus,
    PlayerNext,
    PlayerPause,
    PlayerPlay,
    PlayerPlus,
    PlayerRightSeconds,
    PlayerLeftSeconds,
    PlayerRefresh, AirPlayIcon,
} from "../../assets/files";

import { capitalizeFirstLetter, formatTime } from "../../constants/formats";
import helper from "../../constants/helper";
import { ContentTypes, ProgramPages } from "../../types";
import { LIVE_FAVORITES_UPDATED_EVENT, MOBILE_VERSION } from "../../constants";
import { ISeasonsChapters } from "../Cards/types";

import styles from "./VideoPlayer.module.css";
import { isRealtimeContentType } from "./videoPlayer.utils";

interface VideoPlayerControlsProps {
    togglePlay: () => void;
    toggleFullscreen: () => void;
    handleClose: () => void;
    handleSeek: (newTime: number) => void;
    handleLanguageChange: (event: SelectChangeEvent<string>) => void;
    handleSubtitleChange: (event: SelectChangeEvent<string>) => void;
    handleSubtitleToggle: () => void;
    availableLanguages: string[];
    availableSubtitles: string[];
    selectedLanguage: string;
    selectedSubtitle: string;
    subtitlesEnabled: boolean;
    onAirPlay?: () => void;
    isIPhone?: boolean;
}

const VideoPlayerControls: React.FC<VideoPlayerControlsProps> = ({
    togglePlay,
    toggleFullscreen,
    handleClose,
    handleSeek,
    handleLanguageChange,
    handleSubtitleChange,
    handleSubtitleToggle,
    availableLanguages,
    availableSubtitles,
    selectedLanguage,
    selectedSubtitle,
    subtitlesEnabled,
    onAirPlay,
    isIPhone = false,
}) => {
    const { t } = useTranslation("translations");
    const dispatch = useDispatch();
    const { innerWidth } = useResize();
    const isMobile = innerWidth < MOBILE_VERSION;
    const hasLanguages = availableLanguages.length > 0;
    const hasSubtitles = availableSubtitles.length > 0;
    const hasMediaOptions = hasLanguages || hasSubtitles;

    const {
        id,
        play,
        type,
        progress,
        duration,
        title,
        name,
        serialData,
        isVideoPlayerOpen,
        contentContext,
        eventNotStartedOverlay,
    } = useSelector((state: RootState) => state.videoPlayer);

    const isLiveType = isRealtimeContentType(type);
    const isSerial = type === "SERIE";
    const isMovies = type === "MOVIE";
    const isSportsProgram = contentContext?.page === ProgramPages.SPORTS || type === ProgramPages.SPORTS;
    const isAdultsProgram = contentContext?.page === ProgramPages.ADULT;
    const isLiveFavoriteEligible = isLiveType && !isSportsProgram && !isAdultsProgram;
    const isProgramContextPage = Boolean(
        contentContext &&
        [ProgramPages.LIVE, ProgramPages.SPORTS, ProgramPages.ADULT].includes(
            contentContext.page
        )
    );
    const canOpenProgramLayout = isLiveType && isProgramContextPage;
    const shouldShowProgressBar = !isLiveType && !isSportsProgram;
    const currentTime = (progress / 100) * duration;

    const profileID = localStorage.getItem(helper.EPIC_PROFILE_ID) as string;
    const [triggerGetList] = useLazyGetListQuery();
    const [addToList, { isLoading: isAdding }] = useAddToListMutation();
    const [deleteToList, { isLoading: isDeleting }] = useDeleteToListMutation();

    const [isInMyList, setIsInMyList] = useState(false);
    const [animateStar, setAnimateStar] = useState(false);
    const [selectedSeason, setSelectedSeason] = useState<number>(
        serialData?.seasonNumber ?? 0
    );

    const {
        anchorEl,
        isVisible,
        shouldAnimateOut,
        controlsVisible,
        handleLayout,
        handleClick,
        handleSeekClick,
        handleEpisodeChange,
        handleNextEpisode,
        handleForward30,
        handleBackward30,
        handlePrevEpisode,
        setAnchorEl,
    } = usePlayerControls(id, title, name, type, serialData, isVideoPlayerOpen, handleSeek);

    const {
        channelListScrollRef,
        channelListSentinelRef,
        isChannelListFetching,
        isChannelListLoadingMore,
        handleLayoutClick,
        listTV,
    } = useProgramChannels({
        id,
        isLiveType,
        isProgramContextPage,
        contentContext,
        isVisible,
        canOpenProgramLayout,
        eventNotStartedOverlayVisible: !!eventNotStartedOverlay?.visible,
        handleLayout,
    });

    const seasonOptions =
        serialData?.seasons?.map((s: { season_number: number }) => ({
            label: `${t("Details.season")} ${s.season_number}`,
            value: s.season_number,
        })) ?? [];

    const selectedOption = seasonOptions.find(
        (opt: { value: number }) => opt.value === selectedSeason
    );

    useEffect(() => {
        if (serialData?.seasonNumber) setSelectedSeason(serialData.seasonNumber);
    }, [serialData?.seasonNumber]);

    useEffect(() => {
        if (!id || !profileID || !type || !isVideoPlayerOpen) return;
        if (!isLiveFavoriteEligible) {
            setIsInMyList(false);
            return;
        }

        const checkMyList = async () => {
            try {
                const response = await triggerGetList(profileID).unwrap();
                const list = response?.[type as ContentTypes] as Array<{ id: number | string }> | undefined;
                setIsInMyList(
                    Array.isArray(list) &&
                    list.some((item) => Number(item.id) === Number(id))
                );
            } catch {
                setIsInMyList(false);
            }
        };

        checkMyList();
    }, [id, type, profileID, isVideoPlayerOpen, isLiveFavoriteEligible, triggerGetList]);

    const handleToggleMyList = async () => {
        if (!id || !profileID || !isLiveFavoriteEligible) return;

        const nextIsInMyList = !isInMyList;
        setIsInMyList(nextIsInMyList);
        setAnimateStar(true);

        try {
            if (isInMyList) {
                await deleteToList({params: {profileID}, body: {content: id}}).unwrap();
            } else {
                await addToList({params: {profileID}, body: {content: id}}).unwrap();
            }

            window.dispatchEvent(new Event(LIVE_FAVORITES_UPDATED_EVENT));
        } catch (error) {
            setIsInMyList(!nextIsInMyList);
        } finally {
            setTimeout(() => setAnimateStar(false), 450);
        }
    };

    const handleChangeSeason = (value: number) => {
        dispatch(resetVideo());
        setSelectedSeason(value);
        handleEpisodeChange(value, 1, true);
    };

    const renderSidePanel = (content: React.ReactNode) => (
        <div
            className={clsx(
                styles.serial_episodesWrapper,
                shouldAnimateOut ? styles.animateOut : styles.animateIn
            )}
        >
            <IconButton color="primary" className={styles.iconBtn} onClick={handleLayout}>
                <PlayerBackArrow />
            </IconButton>

            <div className={styles.serial_episodes}>
                <div className={styles.episodesWrapper}>{content}</div>
            </div>
        </div>
    );

    return (
        <>
            {(controlsVisible || isVisible) && (
                <div
                    className={clsx(
                        styles.title,
                        isVisible && styles.titleVisible,
                        controlsVisible && styles.titleHidden
                    )}
                >
                    <IconButton color="primary" className={styles.iconBtn} onClick={handleClose}>
                        <SvgIcon component={isMobile ? BurgerClose : Arrow} />
                    </IconButton>

                    <div className={styles.titleContent}>
                        <Typography
                            variant="h3"
                            className={styles.titleText}
                            fontSize={isMobile ? 14 : 28}
                        >
                            {title}
                        </Typography>

                        {isLiveFavoriteEligible && (
                            <StarIcon
                                className={clsx(
                                    styles.liveStar,
                                    isInMyList && styles.liveStarActive,
                                    animateStar && styles.liveStarSparkle
                                )}
                            />
                        )}
                    </div>

                    {isIPhone && onAirPlay && (
                        <IconButton color="primary" className={styles.iconBtn} onClick={onAirPlay}>
                            <AirPlayIcon />
                        </IconButton>
                    )}
                </div>
            )}

            <div
                className={clsx(
                    styles.wrapperControls,
                    isVisible && styles.wrapperControlsVisible,
                    controlsVisible && styles.wrapperControlsHidden
                )}
            >
                {serialData && (
                    <Stack direction="column" gap={isMobile ? 1 : 2} mb={1}>
                        <Typography variant="h3" className={styles.episodeTitle}>
                            {serialData.episodeTitle}
                        </Typography>
                        <Typography variant="h4">
                            S:{serialData.seasonNumber}, E:{serialData.episodeNumber}
                        </Typography>
                    </Stack>
                )}

                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    mt={3}
                    mb={3}
                >
                    <Stack direction="row" gap={isMobile ? 1 : 2} alignItems="center">
                        {!isLiveType && (
                            <>
                                <IconButton color="primary" className={styles.iconBtn} onClick={togglePlay}>
                                    {play ? <PlayerPause /> : <PlayerPlay />}
                                </IconButton>

                                <IconButton
                                    color="primary"
                                    className={styles.iconBtn}
                                    onClick={isMovies ? handleBackward30 : handlePrevEpisode}
                                >
                                    {!isMovies ? <PlayerBack /> : <PlayerLeftSeconds />}
                                </IconButton>

                                <IconButton
                                    color="primary"
                                    className={styles.iconBtn}
                                    onClick={() => handleSeek(0)}
                                >
                                    <PlayerRefresh />
                                </IconButton>

                                <IconButton
                                    color="primary"
                                    className={styles.iconBtn}
                                    onClick={isMovies ? handleForward30 : handleNextEpisode}
                                >
                                    {!isMovies ? <PlayerNext /> : <PlayerRightSeconds />}
                                </IconButton>
                            </>
                        )}
                    </Stack>

                    <Stack direction="row" gap={isMobile ? 1 : 2} alignItems="center">
                        {isLiveFavoriteEligible && (
                            <IconButton
                                color="primary"
                                className={styles.iconBtn}
                                onClick={handleToggleMyList}
                                disabled={isAdding || isDeleting}
                            >
                                {isInMyList ? <PlayerMinus /> : <PlayerPlus />}
                            </IconButton>
                        )}

                        <IconButton
                            color="primary"
                            className={styles.iconBtn}
                            onClick={toggleFullscreen}
                        >
                            <FullScreen />
                        </IconButton>

                        {!isLiveType && hasMediaOptions && (
                            <IconButton color="primary" className={styles.iconBtn} onClick={handleClick}>
                                <PlayerMessage />
                            </IconButton>
                        )}

                        {isSportsProgram && (
                            <IconButton
                                color="primary"
                                className={styles.iconBtn}
                                title="Add Multi-View Event"
                                data-testid="multiview-add-btn"
                                onClick={() => dispatch(addMultiViewSlot())}
                            >
                                <PlayerLayout />
                            </IconButton>
                        )}

                        {(canOpenProgramLayout || isSerial) && (
                            <IconButton
                                color="primary"
                                className={styles.iconBtn}
                                onClick={handleLayoutClick}
                            >
                                <PlayerLayout />
                            </IconButton>
                        )}
                    </Stack>
                </Stack>

                {shouldShowProgressBar && (
                    <>
                        <div
                            className={styles.progress_wrapper}
                            onClick={(e) => handleSeekClick(e, duration)}
                        >
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                className={styles.progress}
                            />
                        </div>

                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            mt={1}
                            mb={1}
                        >
                            <Typography variant="h4">{formatTime(currentTime)}</Typography>
                            <Typography variant="h4">{formatTime(duration)}</Typography>
                        </Stack>
                    </>
                )}
            </div>

            {isSerial && serialData?.seasonNumber && isVisible &&
                renderSidePanel(
                    <>
                        {seasonOptions.length > 0 && (
                            <CustomSelect
                                value={
                                    selectedOption?.label ??
                                    `${t("Details.season")} ${serialData.seasonNumber}`
                                }
                                options={seasonOptions}
                                onChange={(e: SelectChangeEvent<unknown>) =>
                                    handleChangeSeason(e.target.value as number)
                                }
                            />
                        )}

                        {serialData.chapters?.map((chapter: ISeasonsChapters, i: number) => (
                            <EpisodeCard
                                key={i}
                                data={chapter}
                                onEpisodeChange={handleEpisodeChange}
                                activeNumber={serialData.episodeNumber as number}
                                seasonNumber={serialData.seasonNumber as number}
                                isMobileControls={isMobile}
                            />
                        ))}
                    </>
                )}

            {isLiveType && isVisible &&
                renderSidePanel(
                    <ListTV
                        data={listTV}
                        forPlayer
                        scrollContainerRef={channelListScrollRef}
                        footer={
                            <>
                                {(isChannelListFetching || isChannelListLoadingMore) && (
                                    <Typography variant="body2">{t("Messages.loading")}</Typography>
                                )}
                                <div ref={channelListSentinelRef} style={{ height: 1, width: "100%" }} />
                            </>
                        }
                    />
                )}

            <Popover
                sx={isMobile ? { margin: "0 auto", zIndex: 99999 } : { margin: "-8rem -2rem", zIndex: 99999 }}
                onClose={() => setAnchorEl(null)}
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                disablePortal
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <div className={styles.popoverAudio}>
                    <div className={styles.popoverAudioForm}>
                        {hasLanguages && (
                            <CustomSelect
                                label="Select Language"
                                value={selectedLanguage}
                                onChange={handleLanguageChange}
                                options={availableLanguages.map((lang) => ({
                                    value: lang,
                                    label: capitalizeFirstLetter(lang),
                                }))}
                                MenuProps={{
                                    disablePortal: true,
                                    PaperProps: {
                                        sx: {
                                            zIndex: 99999,
                                            maxHeight: 260,
                                        },
                                    },
                                    MenuListProps: {
                                        sx: {
                                            pointerEvents: "auto",
                                        },
                                    },
                                }}
                            />
                        )}
                        {hasSubtitles && (
                            <>
                                <CustomSelect
                                    label="Change Subtitle"
                                    value={selectedSubtitle}
                                    onChange={handleSubtitleChange}
                                    options={availableSubtitles.map((sub) => ({
                                        value: sub,
                                        label: capitalizeFirstLetter(sub),
                                    }))}
                                    MenuProps={{
                                        disablePortal: true,
                                        PaperProps: {
                                            sx: {
                                                zIndex: 99999,
                                                maxHeight: 260,
                                            },
                                        },
                                        MenuListProps: {
                                            sx: {
                                                pointerEvents: "auto",
                                            },
                                        },
                                    }}
                                />

                                <Button fullWidth color="secondary" variant="outlined" onClick={handleSubtitleToggle}>
                                    {subtitlesEnabled ? "Disable Subtitles" : "Enable Subtitles"}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </Popover>
        </>
    );
};

export default VideoPlayerControls;
